import React from 'react';
import _ from 'lodash';

export function padNumber(number, length = 2, fill = '0') {
  let result = '' + number;
  length -= result.length;
  while (length-- > 0) {
    result = fill + result;
  }
  return result;
}

export function formatNumber(value, excludeGrouping) {
  if (typeof value !== 'number') {
    return '';
  }
  return value.toLocaleString('en-US', {
    style: 'decimal',
    useGrouping: !excludeGrouping
  });
}

export function formatMoney(cents, forceDecimal) {
  if (typeof cents !== 'number') {
    return '';
  }
  let decimals = forceDecimal || cents % 100 !== 0 ? 2 : 0;
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    useGrouping: true,
    minimumFractionDigits: decimals
  });
}

export function formatPhone(phone) {
  phone = '' + phone;
  let s = phone.replace(/\D/g, '');
  if (s.length === 10) {
    phone =
      '(' + s.substring(0, 3) + ') ' + s.substring(3, 6) + '-' + s.substring(6);
  }
  return phone;
}

export function formatAddress(line1, line2, city, state, zip, country) {
  let lines = ['' + line1];
  if (line2 && typeof line2 === 'string') {
    lines.push(line2);
  }
  lines.push(city + ', ' + state + '  ' + zip);
  if (country && typeof country === 'string') {
    lines.push(country);
  }
  return lines;
}

const dateFormatOptions = {
  short: { year: '2-digit', month: '2-digit', day: '2-digit' },
  long: { year: 'numeric', month: 'long', day: 'numeric' }
};

export function formatDate(date, format = 'long') {
  if (!(date instanceof Date)) {
    return '';
  }
  let options = dateFormatOptions[format] || dateFormatOptions['long'];
  return date.toLocaleDateString('en-US', options);
}

// Convert date to ISO9075 MySQL DateTime format string. Pass the date to
// use, something convertable to a Date, or pass nothing to get current date.
export function getIso9075DateString(date) {
  if (!date) {
    date = new Date();
  } else if (!(date instanceof Date)) {
    date = new Date(date);
  }
  // Desired format is "YYYY-MM-DD HH:mm:ss.sss"
  return date
    .toISOString()
    .replace('T', ' ')
    .replace('Z', '');
}

// Convert an ISO9075 MySQL DateTime string to a JS Date object.
export function fromIso9075DateString(value) {
  return value && new Date(value.trim().replace(' ', 'T') + 'Z');
}

export function getTooltipMessage(objects, getAuthor) {
  if (!objects || !objects.length) {
    return null;
  }

  let length = 10;

  if (objects.length < length) {
    length = objects.length;
  }

  let elements = [];

  for (let i = 0; i < length; i++) {
    elements.push(<div key={'a' + i}>{getAuthor(objects[i])}</div>);
  }

  if (objects.length > 10) {
    elements.push(
      <div key={'a+'}>{`...And ${objects.length - 10} more.`}</div>
    );
  }

  return elements.length === 1 ? elements[0] : elements;
}

export function isUserAdmin(user) {
  return user && user.isAdmin;
}

export function canDeletePostOrComment(user, item) {
  // If admin, they can always delete.
  if (isUserAdmin(user)) {
    return true;
  }

  // Check if current user owns the post/comment.
  return item && item.byMe;
}

export function isMobile(url) {
  return url.includes('mobile');
}

export function isGamePage(url) {
  return (
    (url.includes('/mobile/games') && !url.endsWith('/mobile/games')) ||
    (url.includes('/mobile/tournaments') &&
      !url.endsWith('/mobile/tournaments'))
  );
}

export function getUniqueCharacters(value) {
  return _.uniq(value.split(''));
}

export function validateUniqueCharacters(value, length) {
  return getUniqueCharacters(value).length >= length;
}

// Return a copy of the object with a property recursively removed.
export function omitDeep(object, property) {
  return JSON.parse(
    JSON.stringify(
      object,
      (key, value) => (key === property ? undefined : value)
    )
  );
}

export function htmlEncodeCharacters(string, chars = '&<>"\'/') {
  const regex = new RegExp('[' + chars + ']', 'g');
  return string.replace(regex, function(match) {
    return '&#x' + match.charCodeAt(0).toString(16) + ';';
  });
}

export function htmlDecode(string) {
  const regex = new RegExp('&#x([A-Fa-f0-9]{1,2});', 'g');
  return string.replace(regex, function(match, hex) {
    return String.fromCharCode(parseInt(hex, 16));
  });
}

export function parseQuery(queryString) {
  let query = {};
  let pairs = (queryString[0] === '?'
    ? queryString.substr(1)
    : queryString
  ).split('&');
  for (let i = 0; i < pairs.length; i++) {
    let pair = pairs[i].split('=');
    if (pair[0]) {
      query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    }
  }
  return query;
}

export function buildQuery(queryObject) {
  let parts = [];
  for (var key in queryObject) {
    if (key !== '' && queryObject.hasOwnProperty(key)) {
      let value = queryObject[key];
      if (value === null || value === undefined) {
        value = '';
      }
      parts.push(
        encodeURIComponent(key) + '=' + encodeURIComponent('' + value)
      );
    }
  }
  parts.sort();
  return parts.join('&');
}

export function replaceAll(string, target, replacement) {
  return string.split(target).join(replacement);
}

export function safelyCloseWebSocketLink(wsLink) {
  if (wsLink && wsLink.subscriptionClient) {
    const subClient = wsLink.subscriptionClient;
    // Prevent any reconnection loops now that we want this socket closed
    subClient.reconnect = false;
    subClient.clearTryReconnectTimeout();

    // If the socket is in a bad state, any attempts to unsubscribe may throw.
    // Manually unsubscribe everything so unmounting components do not throw.
    Object.keys(subClient.operations).forEach(subId => {
      try {
        subClient.unsubscribe(subId);
      } catch (e) {
        console.warn(e);
      }
    });

    try {
      // Attempt to force-close the socket
      subClient.close(true);
    } catch (e) {
      console.warn(e);
      try {
        // If force-close fails, try a normal close to at least clear the client
        subClient.close(false);
      } catch (ex) {
        console.warn(ex);
      }
    }
  }
}

export function precisionRound(number, precision) {
  var shift = function(number, precision, reverseShift) {
    if (reverseShift) {
      precision = -precision;
    }
    var numArray = ('' + number).split('e');
    return +(
      numArray[0] +
      'e' +
      (numArray[1] ? +numArray[1] + precision : precision)
    );
  };
  return shift(Math.round(shift(number, precision, false)), precision, true);
}



// WEBPACK FOOTER //
// ./src/utils/functions.js